import { InternalPhoneNumberAdminViewDto } from '@act/shared/data-transfer-objects';
import { AdminToolSkeleton } from '../AdminToolSkeleton';
import { getUsers, userAdminSelectors } from 'redux/slices/userAdminSlice';
import { internalPhoneNumbersSelectors } from 'redux/slices/internalPhoneNumberSlice';
import { PaginatedTable, PaginatedTableProps } from '@act/storybook';
import { useEffect, useMemo, useState } from 'react';
import { PhoneNumbersTableColumns } from './PhoneNumbersTableColumns';
import { IndexChildContainer } from 'react-components/member-index/indexContainer';
import { TextField } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { PhoneNumberCreateEdit } from './PhoneNumberCreateEdit';
import { useAppDispatch } from 'redux/store-hooks';

export const PhoneNumbersTable = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [editPhoneOpen, setEditPhoneOpen] = useState(false);
  const [createNewOpen, setCreateNewOpen] = useState(false);
  const [selectedPhone, setSelectedPhone] =
    useState<InternalPhoneNumberAdminViewDto | null>();
  const dispatch = useAppDispatch();
  const allInternalNumbers =
    internalPhoneNumbersSelectors.useSelectAllInternalNumbers();
  const internalNumbers = allInternalNumbers.ids.map(
    id => allInternalNumbers.entities[id]
  );
  const users = userAdminSelectors.useSelectAll();
  const columns = useMemo(() => {
    return PhoneNumbersTableColumns(users);
  }, [users]);

  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) {
      return internalNumbers;
    }

    const searchLowerCase = searchTerm.toLowerCase();

    return internalNumbers.filter(number => {
      const displayName = `${number.displayName}`.toLowerCase();
      const description = `${number.description || ''}`.toLowerCase();
      const phoneNumber = `${number.number || ''}`.toLowerCase();

      return (
        displayName.includes(searchLowerCase) ||
        description.includes(searchLowerCase) ||
        phoneNumber.includes(searchLowerCase)
      );
    });
  }, [internalNumbers, searchTerm]);

  const rowCallback = (row: InternalPhoneNumberAdminViewDto) => {
    setSelectedPhone(row);
    setEditPhoneOpen(true);
  };

  const setFormOpen = (value: boolean) => {
    if (!value) {
      setEditPhoneOpen(false);
      setSelectedPhone(null);
    }
  };

  const PHONE_NUMBERS_ADMIN_TABLE_PROPS: PaginatedTableProps<InternalPhoneNumberAdminViewDto> =
    {
      data: filteredData,
      columnConfig: columns,
      inMemoryPagination: true,
      onRowClick: row => rowCallback(row),
    };

  useEffect(() => {
    dispatch(getUsers({}));
  }, []);

  return (
    <AdminToolSkeleton
      title={'Phone Number Management'}
      toolDescription={'View, Add, or Modify Phone Numbers Here'}
      buttonContent={' Add new number '}
      onClick={() => setCreateNewOpen(true)}
      startIcon={<AddIcon sx={{ ml: 1 }} />}
      variant="contained"
    >
      <TextField
        label="Search by Name or Phone number"
        variant="outlined"
        value={searchTerm}
        onChange={e => setSearchTerm(e.target.value)}
        fullWidth
        margin="normal"
        sx={{ backgroundColor: '#ffffff' }}
      />
      <IndexChildContainer>
        <PaginatedTable {...PHONE_NUMBERS_ADMIN_TABLE_PROPS} />
      </IndexChildContainer>
      {(createNewOpen || editPhoneOpen) && (
        <PhoneNumberCreateEdit
          setOpen={createNewOpen ? setCreateNewOpen : setFormOpen}
          open={createNewOpen || editPhoneOpen}
          users={users}
          isEdit={editPhoneOpen}
          selectedPhone={selectedPhone}
        />
      )}
    </AdminToolSkeleton>
  );
};
